import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, setDoc, deleteDoc, increment, collection, getDocs, query, orderBy, writeBatch, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import ResourceCard from '../components/ResourceCard';

const DEFAULT_TRACK = {
  id: '',
  name: '',
  description: '',
  organizationId: '',
  createdBy: {
    userId: '',
    name: '',
    role: '',
    dateCreated: null
  },
  lastModified: null,
  status: 'draft', // draft, published, archived
  
  // Target Audience Settings
  targetAudience: {
    maturityLevels: {
      blue: false,
      green: false,
      yellow: false
    },
    missionalScale: {
      min: 1,
      max: 5
    },
    recommendedPace: {
      type: 'flexible', // flexible, structured, intensive
      sessionsPerWeek: 1,
      hoursPerSession: 1,
      totalWeeks: 1
    },
    prerequisites: [],
    targetGroup: [] // e.g., ['leaders', 'volunteers', 'staff']
  },

  // Content Structure
  contentPieces: [],
  structure: {
    isSequential: true,
    hasPrerequisites: false,
    allowSkipping: false,
    estimatedDuration: 0, // in minutes
    totalPieces: 0,
    completionRequirements: {
      minimumScore: 0,
      requiredPieces: [],
      timeLimit: null
    }
  },

  // Visibility and Access
  visibility: {
    isPublic: false,
    orgOnly: true,
    restrictedTo: [], // specific groups or roles
    password: null
  },

  // Metadata
  metadata: {
    language: 'en',
    tags: [],
    category: '',
    difficulty: 'beginner',
    estimatedDuration: 0,
    lastUpdated: null,
    version: '1.0',
    authors: [],
    resources: []
  },

  // Progress Tracking
  stats: {
    totalSubscribers: 0,
    activeUsers: 0,
    completionRate: 0,
    averageRating: 0,
    totalViews: 0,
    averageTimeToComplete: 0,
    feedback: []
  },

  // Engagement Features
  features: {
    hasDiscussion: false,
    hasQuizzes: false,
    hasCertificates: false,
    hasDeadlines: false,
    supportsMobile: true,
    supportsOffline: false
  },

  // Schedule
  schedule: {
    startDate: null,
    endDate: null,
    sessions: [],
    reminderFrequency: null
  },

  // Additional Settings
  settings: {
    allowComments: true,
    allowSharing: true,
    allowDownloads: false,
    requireSignIn: true,
    trackProgress: true,
    sendReminders: false
  }
};

const ViewContentTrack = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [track, setTrack] = useState(DEFAULT_TRACK);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);

  useEffect(() => {
    let mounted = true;
    let retryTimeout;

    const fetchTrackAndResources = async () => {
      if (!trackId) {
        setError('No track ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
        
        if (!mounted) return;

        if (!trackDoc.exists()) {
          setError('Track not found');
          setLoading(false);
          return;
        }

        const trackData = trackDoc.data();
        
        // Deep merge default values with actual data
        const processedTrack = {
          ...DEFAULT_TRACK,
          id: trackDoc.id,
          name: trackData.name || 'Untitled Track',
          description: trackData.description || '',
          organizationId: trackData.organizationId || '',
          trackType: trackData.trackType || 'standard', // Load trackType
          targetAgeRange: trackData.targetAgeRange || '', // Load targetAgeRange for children's tracks
          createdBy: {
            ...DEFAULT_TRACK.createdBy,
            ...trackData.createdBy
          },
          lastModified: trackData.lastModified || null,
          status: trackData.status || 'draft',
          targetAudience: {
            ...DEFAULT_TRACK.targetAudience,
            ...trackData.targetAudience,
            maturityLevels: {
              ...DEFAULT_TRACK.targetAudience.maturityLevels,
              ...trackData.targetAudience?.maturityLevels
            },
            missionalScale: {
              ...DEFAULT_TRACK.targetAudience.missionalScale,
              ...trackData.targetAudience?.missionalScale
            },
            recommendedPace: {
              ...DEFAULT_TRACK.targetAudience.recommendedPace,
              ...trackData.targetAudience?.recommendedPace
            },
            prerequisites: trackData.targetAudience?.prerequisites || [],
            targetGroup: trackData.targetAudience?.targetGroup || []
          },
          structure: {
            ...DEFAULT_TRACK.structure,
            ...trackData.structure,
            completionRequirements: {
              ...DEFAULT_TRACK.structure.completionRequirements,
              ...trackData.structure?.completionRequirements
            }
          },
          visibility: {
            ...DEFAULT_TRACK.visibility,
            ...trackData.visibility
          },
          metadata: {
            ...DEFAULT_TRACK.metadata,
            ...trackData.metadata
          },
          contentPieces: trackData.contentPieces || [],
          stats: {
            ...DEFAULT_TRACK.stats,
            ...trackData.stats
          },
          features: {
            ...DEFAULT_TRACK.features,
            ...trackData.features
          },
          schedule: {
            ...DEFAULT_TRACK.schedule,
            ...trackData.schedule
          },
          settings: {
            ...DEFAULT_TRACK.settings,
            ...trackData.settings
          }
        };

        console.log('Processed track data:', processedTrack);
        setTrack(processedTrack);
        
        // Check subscription status (wrap in try-catch to handle permissions)
        try {
          if (currentUser?.uid) {
            const userTrackDoc = await getDoc(
              doc(db, 'users', currentUser.uid, 'subscribedTracks', trackId)
            );
            setIsSubscribed(userTrackDoc.exists());

            // Get progress if subscribed
            if (userTrackDoc.exists()) {
              const progress = userTrackDoc.data().progress || 0;
              setCurrentProgress(progress);
            }
          }
        } catch (subscriptionError) {
          console.warn('Error fetching subscription status (may be a permissions issue):', subscriptionError);
          // Continue without subscription status
          setIsSubscribed(false);
        }

        // Fetch resources from subcollection
        // Note: Fetching without orderBy to avoid index requirements, then sorting client-side
        try {
          const resourcesQuery = collection(db, 'contentTracks', trackId, 'resources');
          const resourcesSnapshot = await getDocs(resourcesQuery);
          const resourcesList = resourcesSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .sort((a, b) => {
              // Sort by displayOrder (ascending), with undefined/null values last
              const orderA = a.displayOrder ?? 999;
              const orderB = b.displayOrder ?? 999;
              return orderA - orderB;
            });

          console.log('Fetched resources:', resourcesList);
          setResources(resourcesList);
        } catch (resourceError) {
          console.warn('Error fetching resources (may be a permissions issue):', resourceError);
          // Continue without resources - user can still view the track
          setResources([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching track and resources:', err);
        if (!mounted) return;

        setError('Failed to load track content');
        setLoading(false);

        // Retry logic
        if (retryCount < 3) {
          retryTimeout = setTimeout(() => {
            if (mounted) {
              setRetryCount(prev => prev + 1);
              fetchTrackAndResources();
            }
          }, 2000 * (retryCount + 1)); // Exponential backoff
        }
      }
    };

    if (trackId) {
      fetchTrackAndResources();
    }

    return () => {
      mounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [trackId, currentUser, retryCount]);

  const handleSubscribe = async () => {
    try {
      // Add track to user's subscribed tracks
      await setDoc(
        doc(db, 'users', currentUser.uid, 'subscribedTracks', trackId),
        {
          subscribedAt: new Date(),
          progress: 0,
          lastAccessed: new Date()
        }
      );

      // Update track stats
      await updateDoc(doc(db, 'contentTracks', trackId), {
        'stats.totalSubscribers': increment(1)
      });

      setIsSubscribed(true);
    } catch (error) {
      console.error('Error subscribing to track:', error);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      // Remove track from user's subscribed tracks
      await deleteDoc(doc(db, 'users', currentUser.uid, 'subscribedTracks', trackId));

      // Update track stats
      await updateDoc(doc(db, 'contentTracks', trackId), {
        'stats.totalSubscribers': increment(-1)
      });

      setIsSubscribed(false);
    } catch (error) {
      console.error('Error unsubscribing from track:', error);
    }
  };

  const handleEdit = (resourceId) => {
    // Use children's edit route if this is a children's track
    if (track.trackType === 'children') {
      navigate(`/content-tracks/${trackId}/resources/${resourceId}/children/edit`);
    } else {
      navigate(`/content-tracks/${trackId}/resources/${resourceId}/edit`);
    }
  };

  const handleDuplicate = async (resourceId) => {
    try {
      // Fetch the original resource
      const resourceDoc = await getDoc(
        doc(db, 'contentTracks', trackId, 'resources', resourceId)
      );

      if (!resourceDoc.exists()) {
        alert('Resource not found');
        return;
      }

      const originalData = resourceDoc.data();

      // Create duplicate with all metadata fields except title, description, articleContent, and url
      // The URL will be generated when the new resource is created
      const duplicateData = {
        type: originalData.type || 'video',
        url: '', // Clear URL - will be generated when created
        author: originalData.author || '',
        authorWebsite: originalData.authorWebsite || '',
        duration: originalData.duration || '',
        tags: originalData.tags || [],
        categories: originalData.categories || [],
        transcription: originalData.transcription || '',
        keyTopics: originalData.keyTopics || [],
        targetAudience: originalData.targetAudience || {
          maturityLevel: { blue: false, green: false, yellow: false },
          relationshipContext: [],
          lifeStage: []
        },
        contentDetails: originalData.contentDetails || {
          platform: '',
          language: 'en',
          difficulty: 'beginner',
          format: '',
          length: ''
        },
        missionalRange: originalData.missionalRange || { min: -10, max: 9 },
        relevantScaleLevels: originalData.relevantScaleLevels || [],
        // New fields for duplicate - clear content fields
        title: '', // Clear title
        description: '', // Clear description (articleContent is not included)
        dateCreated: serverTimestamp(),
        lastModified: serverTimestamp(),
        createdBy: {
          userId: currentUser?.uid || '',
          name: userData?.name || '',
          role: userData?.role || ''
        },
        displayOrder: 999 // Default order at end of list
      };

      // Add the duplicate resource
      await addDoc(
        collection(db, 'contentTracks', trackId, 'resources'),
        duplicateData
      );

      // Refresh the resources list
      const resourcesRef = collection(db, 'contentTracks', trackId, 'resources');
      const resourcesQuery = query(resourcesRef, orderBy('displayOrder', 'asc'));
      const resourcesSnapshot = await getDocs(resourcesQuery);
      const resourcesData = resourcesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setResources(resourcesData);
    } catch (error) {
      console.error('Error duplicating resource:', error);
      alert(`Failed to duplicate resource: ${error.message}`);
    }
  };

  const handleDeleteClick = (resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    setResourceToDelete(resource);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!resourceToDelete) return;

    try {
      await deleteDoc(
        doc(db, 'contentTracks', trackId, 'resources', resourceToDelete.id)
      );

      // Refresh the resources list
      const resourcesRef = collection(db, 'contentTracks', trackId, 'resources');
      const resourcesQuery = query(resourcesRef, orderBy('displayOrder', 'asc'));
      const resourcesSnapshot = await getDocs(resourcesQuery);
      const resourcesData = resourcesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setResources(resourcesData);

      // Close modal
      setDeleteModalOpen(false);
      setResourceToDelete(null);
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert(`Failed to delete resource: ${error.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setResourceToDelete(null);
  };

  const handleReorder = async (resourceId, direction) => {
    const currentIndex = resources.findIndex(r => r.id === resourceId);
    if (currentIndex === -1) return;

    const newResources = [...resources];
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Swap positions in array
    [newResources[currentIndex], newResources[targetIndex]] = 
    [newResources[targetIndex], newResources[currentIndex]];

    // Assign sequential displayOrder to all resources
    newResources.forEach((r, idx) => { r.displayOrder = idx; });

    setResources(newResources);

    // Persist all displayOrder values in a batch
    const batch = writeBatch(db);
    newResources.forEach((r, idx) => {
      batch.update(
        doc(db, 'contentTracks', track.id, 'resources', r.id),
        { displayOrder: idx }
      );
    });

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error updating resource order:', error);
      // Revert the state if the update fails
      setResources(resources);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0076BE] mb-4"></div>
            <p className="text-gray-600">Loading track...</p>
            {retryCount > 0 && (
              <p className="text-sm text-gray-500 mt-2">
                Retry attempt {retryCount} of 3...
              </p>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center p-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
              <div className="text-red-600 font-bold mb-2">Error</div>
              <div className="text-gray-600 mb-4">{error}</div>
              {retryCount < 3 && (
                <button
                  onClick={() => setRetryCount(prev => prev + 1)}
                  className="bg-[#0076BE] text-white px-4 py-2 rounded hover:bg-[#0065a3] transition-colors"
                >
                  Try Again
                </button>
              )}
              {retryCount >= 3 && (
                <div className="text-sm text-gray-500">
                  Maximum retry attempts reached. Please refresh the page or try again later.
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Wayfinding: same labels as edit page where possible */}
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <button
              type="button"
              onClick={() => navigate('/content-tracks')}
              className="text-gray-700 hover:text-gray-900 font-medium inline-flex items-center gap-2 text-sm"
            >
              <span aria-hidden="true">←</span>
              Back to all Content Tracks
            </button>
            <span className="hidden sm:inline text-gray-300 select-none" aria-hidden="true">
              |
            </span>
            <button
              type="button"
              onClick={() => navigate(`/content-tracks/${trackId}/edit`)}
              className="text-sm text-[#0076BE] hover:text-[#005c94] font-medium hover:underline"
            >
              Edit track (add resources, bulk upload, reorder)
            </button>
          </div>

          <div className="rounded-lg border border-blue-100 bg-blue-50/80 px-4 py-3 text-sm text-gray-700">
            <span className="font-semibold text-gray-900">Track overview</span>
            {' — '}
            Preview what is in this track to decide if it fits a disciple. To change content, use Edit track.
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex flex-col gap-2 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">{track.name}</h1>
              {track.trackType === 'children' && (
                <span className="w-fit px-3 py-1 text-sm font-medium bg-[#0076BE] text-white rounded-full">
                  Children&apos;s Resources
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                onClick={() => navigate(`/content-tracks/${trackId}/edit`)}
                className="px-4 py-2 text-white bg-[#0076BE] rounded hover:bg-[#005c94]"
              >
                Edit track
              </button>
            </div>
          </div>
        </div>

        {/* Track Content Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold mb-1 text-gray-900">Track content</h2>
          <p className="text-sm text-gray-500 mb-4">
            Ordered list of resources in this track. Links open in a new tab.
          </p>
          {resources.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-gray-600 text-sm">
              <p className="mb-3">No resources in this track yet.</p>
              <button
                type="button"
                onClick={() => navigate(`/content-tracks/${trackId}/edit`)}
                className="inline-flex items-center justify-center px-4 py-2 rounded-md text-white bg-[#0076BE] hover:bg-[#005c94] text-sm font-medium"
              >
                Go to Edit track to add resources
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {resources.map((resource, index) => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  number={index + 1}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDeleteClick}
                  onReorder={handleReorder}
                  isFirst={index === 0}
                  isLast={index === resources.length - 1}
                  previewMode
                />
              ))}
            </div>
          )}
        </div>

        {/* Track Information Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-1 text-gray-900">Track information</h2>
          <p className="text-sm text-gray-500 mb-4">Description and context mentors use when choosing a track.</p>
          <div className="space-y-4">
            <p className="text-gray-600">{track.description}</p>
            
            {/* Add other track information as needed */}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && resourceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Resource
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{resourceToDelete.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ViewContentTrack; 